import React, { useState } from 'react';
import './PositionsList.css';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import Badge from 'react-bootstrap/Badge';
import { NumericFormat } from 'react-number-format';

/**
 * PositionsList renders the current broker position snapshot.
 */
export default function PositionsList({ positions }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('symbol');
  const [sortAsc, setSortAsc] = useState(true);

  // Currency codes → symbols (for forex).
  const currencySymbolMap = {
    USD: '$',
    CAD: '$',
    EUR: '€',
    GBP: '£',
    JPY: '¥',
    AUD: 'A$',
    NZD: 'NZ$',
    CHF: 'Fr',
    CNY: '¥',
    HKD: 'HK$',
    SEK: 'kr',
    NOK: 'kr',
    MXN: '$',
  };

  const getSymbol = (pos) => pos?.symbol || pos?.asset?.symbol || '???';
  const normalizeAssetType = (value) => {
    const type = String(value || 'unknown').trim().toLowerCase().replace(/[\s-]+/g, '_');
    if (type === 'crypto_futures' || type === 'perpetual' || type === 'perp') return 'crypto_future';
    if (type === 'futures' || type === 'cont_future' || type === 'continuous_future') return 'future';
    return type || 'unknown';
  };
  const getAssetType = (pos) => normalizeAssetType(
    pos?.asset_type || pos?.asset?.asset_type || pos?.asset?.type || 'unknown'
  );
  const getAssetTypeLabel = (type) => String(type || 'unknown').replace(/_/g, ' ');
  const getRawAssetType = (pos) => (
    pos?.raw_asset_type ||
    pos?.rawAssetType ||
    pos?.asset?.raw_asset_type ||
    pos?.asset?.rawAssetType ||
    pos?.raw_broker_asset_type ||
    pos?.asset?.raw_broker_asset_type ||
    ''
  );
  const getParseWarning = (pos) => (
    pos?.broker_parse_warning ||
    pos?.brokerParseWarning ||
    pos?.asset?.broker_parse_warning ||
    pos?.asset?.brokerParseWarning ||
    ''
  );
  const getNumber = (...values) => {
    for (const value of values) {
      if (value === null || value === undefined || value === '') continue;
      const numeric = Number(value);
      if (Number.isFinite(numeric)) return numeric;
    }
    return null;
  };
  const getQuantity = (pos) => getNumber(
    pos?.quantity,
    pos?.qty,
    pos?.asset?.quantity,
    pos?.asset?.qty
  );
  const getMarketValue = (pos) => getNumber(
    pos?.market_value,
    pos?.marketValue,
    pos?.value,
    pos?.val,
    pos?.asset?.market_value,
    pos?.asset?.marketValue,
    pos?.asset?.value,
    pos?.asset?.val
  );
  const getExpirationValue = (pos) => (
    pos?.expiration ||
    pos?.expiry ||
    pos?.exp ||
    pos?.asset?.expiration ||
    pos?.asset?.expiry ||
    pos?.asset?.exp ||
    ''
  );

  const getUnderlying = (pos) => {
    const assetType = getAssetType(pos);
    const asset = pos?.asset || {};
    const nestedUnderlying = asset?.underlying_symbol || asset?.underlyingSymbol || asset?.underlying_asset?.symbol;
    const symbol =
      (assetType === 'option' && (pos?.underlying_symbol || nestedUnderlying)) ||
      pos?.symbol ||
      asset?.symbol ||
      'Unknown';
    return String(symbol).toUpperCase();
  };

  if (!positions || positions.length === 0) {
    return (
      <Container className="positions-list-robinhood">
        <div>No open positions</div>
      </Container>
    );
  }

  const handleSortClick = (field) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
      return;
    }
    setSortField(field);
    setSortAsc(true);
  };

  const searched = positions.filter((pos) => {
    const query = searchQuery.toUpperCase();
    const searchable = [
      getSymbol(pos),
      getUnderlying(pos),
      pos?.contract_symbol,
      pos?.option_symbol,
    ].filter(Boolean).join(' ').toUpperCase();
    return searchable.includes(query);
  });

  const sorted = [...searched].sort((a, b) => {
    if (sortField === 'quantity') {
      const qtyA = getQuantity(a) ?? 0;
      const qtyB = getQuantity(b) ?? 0;
      const quantityDelta = sortAsc ? qtyA - qtyB : qtyB - qtyA;
      if (quantityDelta !== 0) return quantityDelta;
    }

    const symA = getSymbol(a).toUpperCase();
    const symB = getSymbol(b).toUpperCase();
    if (symA < symB) return sortAsc ? -1 : 1;
    if (symA > symB) return sortAsc ? 1 : -1;
    const contractA = String(a?.contract_symbol || a?.option_symbol || a?.asset?.symbol || '');
    const contractB = String(b?.contract_symbol || b?.option_symbol || b?.asset?.symbol || '');
    return contractA.localeCompare(contractB);
  });

  const getPositionKey = (pos, idx) => [
    getSymbol(pos),
    pos?.contract_symbol || pos?.option_symbol || pos?.asset?.symbol || '',
    getExpirationValue(pos),
    pos?.strike || pos?.asset?.strike || '',
    pos?.option_type || pos?.right || pos?.asset?.option_type || pos?.asset?.right || '',
    getQuantity(pos) ?? '',
    idx,
  ].join('|');

  // 5) Utility: badge color + label
  const getBadgeVariant = (type) => {
    switch (type) {
      case 'stock': return 'success';
      case 'forex': return 'primary';
      case 'crypto':
      case 'crypto_future': return 'warning';
      case 'option': return 'option';
      case 'future': return 'secondary';
      default: return 'unknown';
    }
  };

  const formatQuantity = (pos) => {
    const assetType = getAssetType(pos);
    const quantity = getQuantity(pos) ?? 0;

    const assetUnits = {
      stock: "share",
      option: "contract",
      crypto: "token",
      crypto_future: "token",
      future: "contract",
      forex: "lot"
    };

    // Get the unit of ownership for the given asset
    const unit = assetUnits[assetType] || "unknown asset";

    if (assetType === 'forex') {
      // e.g. "USD" => "$" + 2 decimals
      const currencyCode = pos?.asset?.currency || 'USD';
      const symbol = currencySymbolMap[currencyCode] || '$';
      
      return (
        <NumericFormat
          value={quantity}
          displayType="text"
          thousandSeparator
          decimalScale={2}
          fixedDecimalScale
          prefix={symbol}
        />
      );
    } else {
      // stocks => "1,234 shares"
      // use a regular expression to format the string depending on the unit
      return (
        <>
          <NumericFormat
        value={quantity}
        displayType="text"
        thousandSeparator
          />
          &nbsp;
          {`${unit}${Math.abs(quantity) !== 1 ? "s" : ""}`}
        </>
      );
    }
  };

  const formatExpirationDate = (pos) => {
    const expirationDate = getExpirationValue(pos);
    if (!expirationDate) return 'N/A';
    const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(expirationDate));
    const parsedExpirationDate = dateOnlyMatch
      ? new Date(Number(dateOnlyMatch[1]), Number(dateOnlyMatch[2]) - 1, Number(dateOnlyMatch[3]))
      : new Date(expirationDate);
    if (Number.isNaN(parsedExpirationDate.getTime())) return 'N/A';
    return parsedExpirationDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatStrikePrice = (pos) => {
    const strikePrice = getNumber(pos?.strike, pos?.asset?.strike);
    if (strikePrice === null) return 'N/A';
    return (
      <NumericFormat
        value={strikePrice}
        displayType="text"
        thousandSeparator
        prefix="$"
        decimalScale={2}
        fixedDecimalScale
      />
    );
  };

  const formatMoney = (value) => {
    const numeric = getNumber(value);
    if (numeric === null) return 'N/A';
    return (
      <NumericFormat
        value={numeric}
        displayType="text"
        thousandSeparator
        prefix="$"
        decimalScale={2}
        fixedDecimalScale
      />
    );
  };

  const formatSignedMoney = (value) => {
    const numeric = getNumber(value);
    if (numeric === null) return 'N/A';
    return (
      <span className={numeric > 0 ? 'position-positive' : numeric < 0 ? 'position-negative' : ''}>
        {formatMoney(numeric)}
      </span>
    );
  };

  return (
    <div className="positions-list-robinhood">
      <Container fluid>
        <div className="positions-controls-row d-flex flex-wrap align-items-center mb-3">
          <Form.Group controlId="searchSymbols" className="search-group me-auto mb-2">
            <Form.Control
              type="text"
              placeholder="Search symbol or contract..."
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
          </Form.Group>

          <div className="sort-buttons d-flex mb-2">
            <button
              type="button"
              className="btn btn-sm btn-outline-light me-2"
              onClick={() => handleSortClick('symbol')}
            >
              Sort by Symbol
              {sortField === 'symbol' && (sortAsc ? ' ▲' : ' ▼')}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-light"
              onClick={() => handleSortClick('quantity')}
            >
              Sort by Quantity
              {sortField === 'quantity' && (sortAsc ? ' ▲' : ' ▼')}
            </button>
          </div>
        </div>

        <div className="positions-table-scroll">
          <table className="positions-detail-table">
            <thead>
              <tr>
                <th>Position</th>
                <th>Type</th>
                <th>Qty</th>
                <th>Value</th>
                <th>P/L</th>
                <th>Avg Entry</th>
                <th>Contract</th>
                <th>Strike</th>
                <th>Expiration</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((pos, idx) => {
                const symbol = getSymbol(pos);
                const assetType = getAssetType(pos);
                const badgeVariant = getBadgeVariant(assetType);
                const marketValue = getMarketValue(pos);
                const pnl = getNumber(pos?.pnl, pos?.unrealized_pl, pos?.unrealizedPnl);
                const isOption = assetType === 'option';
                const avgEntry = getNumber(pos?.avg_fill_price, pos?.avgFillPrice);
                const contract = pos?.contract_symbol || pos?.option_symbol || '';
                const contractLabel = contract || (assetType === 'stock' ? 'Stock' : 'N/A');
                const rawAssetType = getRawAssetType(pos);
                const parseWarning = getParseWarning(pos);

                return (
                  <tr key={getPositionKey(pos, idx)}>
                    <td>
                      <div className="positions-symbol-cell">{symbol}</div>
                      {isOption && getUnderlying(pos) !== symbol && (
                        <div className="positions-subtext">Underlying {getUnderlying(pos)}</div>
                      )}
                    </td>
                    <td>
                      <Badge bg={badgeVariant} className="pos-type-badge text-uppercase">
                        {getAssetTypeLabel(assetType)}
                      </Badge>
                      {(assetType === 'unknown' || rawAssetType || parseWarning) && (
                        <div className="positions-subtext positions-raw-broker-type">
                          {rawAssetType ? `Broker type: ${rawAssetType}` : parseWarning || 'Unrecognized broker position'}
                        </div>
                      )}
                    </td>
                    <td>{formatQuantity(pos)}</td>
                    <td>{formatMoney(marketValue)}</td>
                    <td>{formatSignedMoney(pnl)}</td>
                    <td>{formatMoney(avgEntry)}</td>
                    <td>{contractLabel}</td>
                    <td>{isOption ? formatStrikePrice(pos) : 'N/A'}</td>
                    <td>{isOption ? formatExpirationDate(pos) : 'N/A'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Container>
    </div>
  );
}
